import {Component, effect, input, viewChild} from '@angular/core';
import {ConfigMatrix, ConfigMatrixValue} from '../../../api-client-kt/src';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {ColumnDeclaration, Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {Aggregate, getAggregate} from '@mescius/wijmo';
import {RefdataService} from '../../data/portfolio-data-context.service';
import {Stdlib} from '../../lib/stdlib';
import {FlexGrid, FormatItemEventArgs, GroupRow} from '@mescius/wijmo.grid';
import {ColorMap} from '../../lib/color-map';
import {sortBy} from 'es-toolkit';

const FIRST_COLUMN_WIDTH = 100;

export interface KeyNamePair {
    key: string;
    name: string;
}

/**
 * Represents a component that visualizes a configuration matrix with data related to Lines of Business (LoBs) and Offices.
 * The grid can display formatted data, apply aggregate computations, generate heatmaps, and support interactive operations.
 *
 * This component uses a FlexGrid for rendering the matrix and provides capabilities to handle
 * various key and value types of the configuration matrix.
 */
@Component({
    selector: 'app-app-config-matrix',
    imports: [
        WjGridModule
    ],
    templateUrl: './app-config-matrix.component.html',
    styleUrl: './app-config-matrix.component.scss',
})
export class AppConfigMatrixComponent {

    readonly matrix = input.required<ConfigMatrix>();
    readonly values = input.required<ConfigMatrixValue[]>();
    readonly refDataSvc = input.required<RefdataService>();

    protected lobs: KeyNamePair[] = [];
    protected offices: KeyNamePair[] = [];

    private minValue  = Number.MIN_VALUE;
    private maxValue = Number.MAX_VALUE;
    private colorMap: ColorMap | undefined = undefined;

    private readonly grid = viewChild.required(WjFlexGrid);

    protected readonly Wijmo = Wijmo;

    constructor() {
        effect(() => this.getOfficesAndLobs())
        effect(() => this.calculateMinMax());
    }

    private getOfficesAndLobs() {
        const refSvc = this.refDataSvc();
        this.lobs = sortBy(refSvc.lobKeys(). map(key => ({ key, name: refSvc.lookupLobName(key) })), ['name']);
        this.offices = sortBy(refSvc.officeKeys(). map(key => ({ key, name: refSvc.lookupOfficeName(key) })), ['name']);
    }

    protected onGridInitialized(grid: WjFlexGrid) {
        let columnDefs: ColumnDeclaration[] = [];
        let data: any[] = [];
        switch (this.matrix().keyType) {
            case "LobOffice":
                [columnDefs, data] = this.generateColumnsLobAndOffice();
                break;
            case "Lob":
                [columnDefs, data] = this.generateColumnsLob();
                break;
            case "Office":
                [columnDefs, data] = this.generateColumnsOffice();
                break;
        }

        WjGridUtils.addColumnGroups(grid, columnDefs);

        this.updateRowTotals(data);
        grid.itemsSource = data;

        WjGridUtils.autoUnselectWhenFocusLost(grid);
        grid.formatItem.addHandler(this.heatMapFormatHandler.bind(this));

        Stdlib.let(this.aggregateType(), aggregateType =>  {
            grid.columns.forEach((col, idx) => col.aggregate = idx > 0 ? aggregateType : Aggregate.None);
            const gr = new GroupRow(this.readColumnAggregates());
            gr.cssClassAll = 'total-cell';
            gr.isSummary = true;
            grid.columnFooters.rows.push(gr);
        });

        WjGridUtils.registerSelectedCellValueSignal(grid);
    }

    private generateColumnsLobAndOffice(): [ColumnDeclaration[], any[]] {
        const format = this.cellFormat();
        const getValue = this.valueGetter();

        const columnTemplate: Partial<ColumnDeclaration> = {
            align: 'center',
            format: format,
            cssClass: 'gradient-cell',
            aggregate: Aggregate.Avg,
            width: 70,
        };

        const officeColumns: ColumnDeclaration[] = this.offices.map(office => ({
            ...columnTemplate, header: office.name, binding: office.key,
        }));

        const columnDef: ColumnDeclaration[] = [
            { header: "LoB", binding: "lobName", align: 'center', cssClass: 'grid-row-header', isReadOnly: true, width: FIRST_COLUMN_WIDTH },
            { header: "Producing Office", align: 'center', columns: officeColumns }
        ];

        Stdlib.let(this.aggregateType(), aggregate => {
            columnDef.push({ header: "Total", binding: "total", align: 'center', cssClass: 'total-cell', format, isReadOnly: true, aggregate });
        });

        const data = this.lobs.map(lob => {
            const lobValues = this.values().filter(v => v.lobKey === lob.key);
            const rowData = Object.fromEntries(lobValues.map(v => [v.officeKey, getValue(v)]));
            return { lobKey: lob.key, lobName: lob.name, ... rowData };
        });

        return [columnDef, data];
    }

    private generateColumnsLob():  [ColumnDeclaration[], any[]] {
        const format = this.cellFormat();
        const getValue = this.valueGetter();
        const aggregate = this.aggregateType();

        const columnDef: ColumnDeclaration[] = [
            { header: "LoB", binding: "lobName", align: 'center', cssClassAll: 'grid-row-header vector-data', isReadOnly: true, width: FIRST_COLUMN_WIDTH },
            { header: "Value", binding: "value", align: 'center', format, cssClassAll: 'gradient-cell vector-data', aggregate, width: 70 }
        ];

        const data = this.lobs.map(lob => {
            const value = getValue(this.values().find(v => v.lobKey === lob.key && v.officeKey === '*'));
            return { lobName: lob.name, value: value };
        });

        return [columnDef, data];
    }

    private generateColumnsOffice():  [ColumnDeclaration[], any[]] {
        const format = this.cellFormat();
        const getValue = this.valueGetter();
        const aggregate = this.aggregateType();

        const columnDef: ColumnDeclaration[] = [
            { header: "Office", binding: "officeName", align: 'center', cssClassAll: 'grid-row-header vector-data', isReadOnly: true, width: FIRST_COLUMN_WIDTH },
            { header: "Value", binding: "value", align: 'center', format, cssClassAll: 'gradient-cell vector-data', aggregate, width: 70 }
        ];

        const data = this.offices.map(office => {
            const value = getValue(this.values().find(v => v.officeKey === office.key && v.lobKey === '*'));
            return { officeName: office.name, value: value };
        });

        return [columnDef, data];
    }

    private readColumnAggregates(): Record<string, any> {
        // do it for every individual office
        const officeTotals = this.offices.map(office => {
            const aggregateValue = this.values().find(v => v.officeKey == office.key && v.lobKey === '*')?.value
            return [office.key, aggregateValue];
        });

        // and try also to find the total total
        const totalTotal = this.values().find(v => v.officeKey === '*' && v.lobKey === '*')?.value;

        // create a data row with all entries
        return Object.fromEntries([...officeTotals, ['total', totalTotal]]);
    }

    private updateRowTotals(data: any[]): void {
        const aggregateType = this.aggregateType();
        const rowBindings = this.horizontalBindings();

        if (aggregateType) {
            data.forEach(row => {
                const matrixAggregate = this.values().find(v => v.lobKey == row.lobKey && v.officeKey === '*')?.value;
                row.total = matrixAggregate ?? getAggregate(aggregateType, rowBindings.map(binding => row[binding] ?? 0));
            });
        }
    }

    private horizontalBindings(): string[] {
        switch (this.matrix().keyType) {
            case "LobOffice": return this.offices.map(office => office.key);
            default: return ['value'];
        }
    }

    private valueGetter(): (cmv: ConfigMatrixValue | undefined | null) => any {
        return (v: ConfigMatrixValue | undefined | null) => v?.value;
    }

    private cellFormat(): string {
        return this.matrix().displayFormat;
    }

    private nonAggregateValueFilter(): (value: ConfigMatrixValue) => boolean {
        switch (this.matrix().keyType) {
            case "LobOffice":
                return value => value.lobKey !== '*' && value.officeKey !== '*';
            case "Lob":
                return value => value.lobKey !== '*';
            case "Office":
                return value => value.officeKey !== '*';
            default:
                throw new Error(`Unknown key type: ${this.matrix().keyType}`);
        }
    }

    private heatmap(): boolean {
        return this.matrix().valueType === 'Amount' || this.matrix().valueType === 'Percent';
    }

    private aggregateType(): Aggregate | undefined {
        switch (this.matrix().valueType) {
            case 'Amount': return Aggregate.Sum;
            case 'Percent': return Aggregate.Avg;
            case 'Text': return undefined;
            case 'Boolean': return undefined;
        }
    }

    private calculateMinMax() {
        if (this.matrix().valueType === "Amount" || this.matrix().valueType === "Percent") {
            const valuesFlat = this.values()
                .filter(this.nonAggregateValueFilter())
                .map(value => value.value)
                .filter(value => value != null) as number[];

            this.minValue = Math.min(...valuesFlat);
            this.maxValue = Math.max(...valuesFlat);

            if (this.minValue != null && this.maxValue != null) {
                this.colorMap = new ColorMap(this.minValue, this.maxValue, ColorMap.LRColors);
            }
        }
    }

    private heatMapFormatHandler(s: FlexGrid, e: FormatItemEventArgs) {
        const isInputElement = e.cell.innerHTML.indexOf('input') >= 0;
        if (isInputElement) return;

        if (e.panel === s.columnHeaders) {
            const text = e.cell.innerText;
            if (['Producing Office', 'LoB', 'Total'].indexOf(text) >= 0) {
                e.cell.classList.add('accent');
            }
        } if (e.panel === s.cells) {
            if (this.heatmap() && this.colorMap) {
                const isTotalColumn = e.getColumn().binding === 'total'
                if (isTotalColumn) return;

                const col = s.columns[e.col];
                const value = s.getCellData(e.row, e.col, false);
                if (typeof value !== 'number') return;

                const color = this.colorMap.interpolateColor(value);
                e.cell.classList.add('gradient-cell-value');
                e.cell.style.backgroundColor = color;
            }
        }
    }

}
